import { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Wallet,
  Plus,
  Search,
  RefreshCw,
  AlertCircle,
  Eye,
  QrCode,
  ArrowUpRight,
  Building2,
  User,
  Briefcase,
  Landmark,
  ChevronRight,
  Trash2,
} from 'lucide-react';
import { Card, CardBody, CardHeader, CardTitle, Badge, Button, Input, Modal, Select } from '../components/ui';
import { formatCurrency, formatDate, formatDocument } from '../lib/utils';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';

interface Account {
  id: string;
  name: string;
  type: 'BOLSAO' | 'VIRTUAL' | 'REAL';
  status: 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'CLOSED';
  balance: number;
  pixKey?: string;
  pixKeyType?: string;
  provider?: string;
  kycStatus?: string;
  entityId?: string;
  bankConnectionId?: string;
  parentAccountId?: string;
  entity?: {
    id: string;
    name: string;
    document: string;
    type: string;
  };
  bankConnection?: {
    id: string;
    name: string;
    provider: string;
  };
  parentAccount?: {
    id: string;
    name: string;
  };
  user?: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
}

interface Entity {
  id: string;
  name: string;
  document: string;
  type: 'COMPANY' | 'INDIVIDUAL';
  status: string;
}

interface BankConnection {
  id: string;
  name: string;
  provider: string;
  status: string;
  isDefault: boolean;
}

const accountTypeLabels = {
  BOLSAO: 'Conta Principal',
  VIRTUAL: 'Conta Virtual',
  REAL: 'Conta Real',
};

const accountTypeIcons = {
  BOLSAO: Building2,
  VIRTUAL: User,
  REAL: Briefcase,
};

const accountTypeColors = {
  BOLSAO: 'primary',
  VIRTUAL: 'info',
  REAL: 'success',
} as const;

export function Accounts() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [accountToDelete, setAccountToDelete] = useState<Account | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'VIRTUAL' as Account['type'],
    userId: '',
    entityId: '',
    bankConnectionId: '',
    parentAccountId: '',
    // KYC fields for REAL accounts
    cpfCnpj: '',
    email: '',
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['accounts', typeFilter],
    queryFn: async () => {
      const params: Record<string, string> = {};
      if (typeFilter) params.type = typeFilter;
      const response = await api.get(ENDPOINTS.accounts.list, { params });
      return response.data as { accounts: Account[]; total: number };
    },
  });

  const { data: usersData } = useQuery({
    queryKey: ['users-for-accounts'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.users.list);
      return response.data as { users: { id: string; name: string; email: string }[] };
    },
  });

  // Fetch entities for selection
  const { data: entitiesData } = useQuery({
    queryKey: ['entities-for-accounts'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.entities.list);
      return response.data as Entity[];
    },
  });

  // Fetch bank connections for selection
  const { data: bankConnectionsData } = useQuery({
    queryKey: ['bank-connections-for-accounts'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.bankConnections.list);
      return response.data as BankConnection[];
    },
  });

  // Fetch bolsao accounts for parent selection (virtual accounts)
  const { data: bolsaoAccountsData } = useQuery({
    queryKey: ['bolsao-accounts'],
    queryFn: async () => {
      const response = await api.get(ENDPOINTS.accounts.list, { params: { type: 'BOLSAO' } });
      return response.data as { accounts: Account[] };
    },
  });

  const createBolsaoMutation = useMutation({
    mutationFn: async (data: { name: string; entityId?: string; bankConnectionId?: string }) => {
      // Remove empty strings - send only non-empty values
      const payload: { name: string; entityId?: string; bankConnectionId?: string } = { name: data.name };
      if (data.entityId && data.entityId.trim()) payload.entityId = data.entityId;
      if (data.bankConnectionId && data.bankConnectionId.trim()) payload.bankConnectionId = data.bankConnectionId;
      const response = await api.post(ENDPOINTS.accounts.bolsao, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['bolsao-accounts'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const createVirtualMutation = useMutation({
    mutationFn: async (data: { name: string; userId?: string; entityId?: string; parentAccountId?: string }) => {
      // Remove empty strings - send only non-empty values
      const payload: { name: string; userId?: string; entityId?: string; parentAccountId?: string } = { name: data.name };
      if (data.userId && data.userId.trim()) payload.userId = data.userId;
      if (data.entityId && data.entityId.trim()) payload.entityId = data.entityId;
      if (data.parentAccountId && data.parentAccountId.trim()) payload.parentAccountId = data.parentAccountId;
      const response = await api.post(ENDPOINTS.accounts.virtual, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const createRealMutation = useMutation({
    mutationFn: async (data: { name: string; userId: string; entityId?: string; bankConnectionId?: string; cpfCnpj: string; email: string }) => {
      // Remove empty strings - send only non-empty values
      const payload: { name: string; userId: string; cpfCnpj: string; email: string; entityId?: string; bankConnectionId?: string } = { 
        name: data.name,
        userId: data.userId,
        cpfCnpj: data.cpfCnpj,
        email: data.email,
      };
      if (data.entityId && data.entityId.trim()) payload.entityId = data.entityId;
      if (data.bankConnectionId && data.bankConnectionId.trim()) payload.bankConnectionId = data.bankConnectionId;
      const response = await api.post(ENDPOINTS.accounts.real, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (accountId: string) => {
      const response = await api.delete(ENDPOINTS.accounts.delete(accountId));
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['bolsao-accounts'] });
      setAccountToDelete(null);
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'VIRTUAL',
      userId: '',
      entityId: '',
      bankConnectionId: '',
      parentAccountId: '',
      cpfCnpj: '',
      email: '',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.type === 'BOLSAO') {
      createBolsaoMutation.mutate({ 
        name: formData.name,
        entityId: formData.entityId || undefined,
        bankConnectionId: formData.bankConnectionId || undefined,
      });
    } else if (formData.type === 'VIRTUAL') {
      createVirtualMutation.mutate({ 
        name: formData.name, 
        userId: formData.userId || undefined,
        entityId: formData.entityId || undefined,
        parentAccountId: formData.parentAccountId || undefined,
      });
    } else {
      // REAL account requires userId, cpfCnpj and email
      if (!formData.userId) {
        alert('Selecione um usuário para conta real');
        return;
      }
      if (!formData.cpfCnpj) {
        alert('Informe o CPF/CNPJ para conta real');
        return;
      }
      if (!formData.email) {
        alert('Informe o email para conta real');
        return;
      }
      createRealMutation.mutate({ 
        name: formData.name, 
        userId: formData.userId,
        entityId: formData.entityId || undefined,
        bankConnectionId: formData.bankConnectionId || undefined,
        cpfCnpj: formData.cpfCnpj.replace(/\D/g, ''),
        email: formData.email,
      });
    }
  };

  const accounts = data?.accounts || [];
  const filteredAccounts = accounts.filter(account =>
    account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.pixKey?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.entity?.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalBalance = accounts.reduce((sum, acc) => sum + Number(acc.balance), 0);
  const users = usersData?.users || [];
  const entities = entitiesData || [];
  const bankConnections = bankConnectionsData || [];
  const bolsaoAccounts = bolsaoAccountsData?.accounts || [];

  // Filter entities by type based on account type
  const filteredEntities = formData.type === 'BOLSAO' 
    ? entities.filter(e => e.type === 'COMPANY' && e.status === 'ACTIVE')
    : entities.filter(e => e.status === 'ACTIVE');

  // Filter active bank connections
  const activeBankConnections = bankConnections.filter(bc => bc.status === 'ACTIVE');

  const isCreating = createBolsaoMutation.isPending || createVirtualMutation.isPending || createRealMutation.isPending;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <AlertCircle className="w-16 h-16 text-red-400" />
        <h2 className="text-xl font-semibold text-surface-100">Erro ao carregar contas</h2>
        <Button variant="primary" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-surface-100">Contas</h1>
          <p className="text-surface-400">Gerencie as contas da sua empresa</p>
        </div>
        <Button variant="primary" onClick={() => { resetForm(); setIsModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Conta
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card variant="stat">
          <CardBody>
            <p className="text-sm text-surface-400">Saldo Total</p>
            <p className="text-2xl font-bold text-surface-100 mt-1">
              {isLoading ? '...' : formatCurrency(totalBalance)}
            </p>
          </CardBody>
        </Card>
        <Card variant="stat">
          <CardBody>
            <p className="text-sm text-surface-400">Contas Bolsão</p>
            <p className="text-2xl font-bold text-primary-400 mt-1">
              {accounts.filter(a => a.type === 'BOLSAO').length}
            </p>
          </CardBody>
        </Card>
        <Card variant="stat">
          <CardBody>
            <p className="text-sm text-surface-400">Contas Virtuais</p>
            <p className="text-2xl font-bold text-accent-400 mt-1">
              {accounts.filter(a => a.type === 'VIRTUAL').length}
            </p>
          </CardBody>
        </Card>
        <Card variant="stat">
          <CardBody>
            <p className="text-sm text-surface-400">Contas Reais</p>
            <p className="text-2xl font-bold text-green-400 mt-1">
              {accounts.filter(a => a.type === 'REAL').length}
            </p>
          </CardBody>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardBody>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                placeholder="Buscar por nome, chave Pix ou entidade..."
                leftIcon={<Search className="w-4 h-4" />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              options={[
                { value: '', label: 'Todos os tipos' },
                { value: 'BOLSAO', label: 'Conta Principal' },
                { value: 'VIRTUAL', label: 'Conta Virtual' },
                { value: 'REAL', label: 'Conta Real' },
              ]}
              className="w-48"
            />
            <Button variant="secondary" onClick={() => refetch()} disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Accounts List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-primary-400" />
            Lista de Contas
            <Badge variant="info">{filteredAccounts.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="animate-pulse bg-surface-800 rounded h-20" />
              ))}
            </div>
          ) : filteredAccounts.length === 0 ? (
            <div className="text-center py-12 text-surface-400">
              <Wallet className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma conta encontrada</p>
            </div>
          ) : (
            <div className="divide-y divide-surface-800">
              {filteredAccounts.map(account => {
                const Icon = accountTypeIcons[account.type];
                return (
                  <div
                    key={account.id}
                    className="flex items-center justify-between px-6 py-4 hover:bg-surface-800/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        account.type === 'BOLSAO' ? 'bg-primary-500/20 text-primary-400' :
                        account.type === 'VIRTUAL' ? 'bg-accent-500/20 text-accent-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-medium text-surface-100">{account.name}</p>
                        <div className="flex items-center gap-2 text-sm text-surface-500">
                          <span>{accountTypeLabels[account.type]}</span>
                          {account.entity && (
                            <>
                              <ChevronRight className="w-3 h-3" />
                              <span>{account.entity.name}</span>
                            </>
                          )}
                          {account.parentAccount && (
                            <>
                              <ChevronRight className="w-3 h-3" />
                              <span className="text-primary-400">↳ {account.parentAccount.name}</span>
                            </>
                          )}
                          {account.user && (
                            <>
                              <ChevronRight className="w-3 h-3" />
                              <span>{account.user.name}</span>
                            </>
                          )}
                        </div>
                        {account.pixKey && (
                          <p className="text-xs text-surface-500 font-mono">
                            Pix: {account.pixKey}
                          </p>
                        )}
                        {account.bankConnection && (
                          <p className="text-xs text-surface-500 flex items-center gap-1">
                            <Landmark className="w-3 h-3" />
                            {account.bankConnection.name} ({account.bankConnection.provider})
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-semibold text-surface-100">
                          {formatCurrency(Number(account.balance))}
                        </p>
                        <p className="text-xs text-surface-500">
                          {formatDate(account.createdAt)}
                        </p>
                      </div>
                      <Badge variant={account.status === 'ACTIVE' ? 'success' : account.status === 'PENDING' ? 'warning' : 'error'}>
                        {account.status}
                      </Badge>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedAccount(account)} title="Ver detalhes">
                          <Eye className="w-4 h-4" />
                        </Button>
                        {account.pixKey && (
                          <Button variant="ghost" size="sm" title="Gerar QR Code">
                            <QrCode className="w-4 h-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" title="Transferir">
                          <ArrowUpRight className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          title="Excluir conta"
                          onClick={() => setAccountToDelete(account)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Create Account Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); resetForm(); }}
        title="Nova Conta"
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
            label="Tipo de Conta"
            value={formData.type}
            onChange={(e) => setFormData({ ...formData, type: e.target.value as Account['type'], parentAccountId: '', bankConnectionId: '' })}
            options={[
              { value: 'BOLSAO', label: 'Conta Principal (Bolsão)' },
              { value: 'VIRTUAL', label: 'Conta Virtual' },
              { value: 'REAL', label: 'Conta Real (com KYC)' },
            ]}
          />
          
          <Input
            label="Nome da Conta"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          {/* Entity Selection */}
          <Select
            label={formData.type === 'BOLSAO' ? 'Entidade (PJ obrigatória)' : 'Entidade (opcional)'}
            value={formData.entityId}
            onChange={(e) => setFormData({ ...formData, entityId: e.target.value })}
            options={[
              { value: '', label: 'Selecione uma entidade' },
              ...filteredEntities.map(e => ({ 
                value: e.id, 
                label: `${e.name} (${formatDocument(e.document)})` 
              })),
            ]}
            required={formData.type === 'BOLSAO'}
          />

          {/* Bank Connection Selection - for BOLSAO and REAL */}
          {(formData.type === 'BOLSAO' || formData.type === 'REAL') && (
            <Select
              label="Conexão Bancária"
              value={formData.bankConnectionId}
              onChange={(e) => setFormData({ ...formData, bankConnectionId: e.target.value })}
              options={[
                { value: '', label: 'Usar conexão padrão' },
                ...activeBankConnections.map(bc => ({ 
                  value: bc.id, 
                  label: `${bc.name} (${bc.provider})${bc.isDefault ? ' ★' : ''}` 
                })),
              ]}
            />
          )}

          {/* Parent Account Selection - for VIRTUAL */}
          {formData.type === 'VIRTUAL' && (
            <Select
              label="Conta Pai (Bolsão)"
              value={formData.parentAccountId}
              onChange={(e) => setFormData({ ...formData, parentAccountId: e.target.value })}
              options={[
                { value: '', label: 'Selecione a conta pai' },
                ...bolsaoAccounts.map(a => ({ 
                  value: a.id, 
                  label: `${a.name}${a.entity ? ` - ${a.entity.name}` : ''}` 
                })),
              ]}
            />
          )}

          {/* User Selection - for VIRTUAL and REAL */}
          {(formData.type === 'VIRTUAL' || formData.type === 'REAL') && (
            <Select
              label={formData.type === 'REAL' ? 'Usuário (obrigatório)' : 'Usuário (opcional)'}
              value={formData.userId}
              onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
              options={[
                { value: '', label: 'Selecione um usuário' },
                ...users.map(u => ({ value: u.id, label: `${u.name} (${u.email})` })),
              ]}
              required={formData.type === 'REAL'}
            />
          )}

          {/* KYC Fields - for REAL accounts */}
          {formData.type === 'REAL' && (
            <>
              <Input
                label="CPF/CNPJ (obrigatório)"
                value={formData.cpfCnpj}
                onChange={(e) => setFormData({ ...formData, cpfCnpj: e.target.value.replace(/\D/g, '') })}
                placeholder="Apenas números"
                maxLength={14}
                required
              />
              <Input
                label="Email (obrigatório)"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@exemplo.com"
                required
              />
            </>
          )}

          <div className="p-3 bg-surface-800 rounded-lg text-sm text-surface-400">
            {formData.type === 'BOLSAO' && (
              <div className="space-y-1">
                <p>A conta principal centraliza todos os recebimentos da empresa.</p>
                <p className="text-xs">• Requer uma entidade PJ (empresa)</p>
                <p className="text-xs">• Pode ter múltiplas contas virtuais filhas</p>
              </div>
            )}
            {formData.type === 'VIRTUAL' && (
              <div className="space-y-1">
                <p>Contas virtuais são internas e não requerem KYC pesado.</p>
                <p className="text-xs">• Herdam a conexão bancária da conta pai</p>
                <p className="text-xs">• Podem ser vinculadas a funcionários, clientes ou fornecedores</p>
              </div>
            )}
            {formData.type === 'REAL' && (
              <div className="space-y-1">
                <p>Contas reais requerem verificação KYC completa.</p>
                <p className="text-xs">• Documentos + prova de vida</p>
                <p className="text-xs">• Podem emitir cobranças próprias</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" isLoading={isCreating}>
              Criar Conta
            </Button>
          </div>
        </form>
      </Modal>

      {/* Account Details Modal */}
      <Modal
        isOpen={!!selectedAccount}
        onClose={() => setSelectedAccount(null)}
        title="Detalhes da Conta"
        size="lg"
      >
        {selectedAccount && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-surface-500">Nome</p>
                <p className="text-surface-100 font-medium">{selectedAccount.name}</p>
              </div>
              <div>
                <p className="text-sm text-surface-500">Tipo</p>
                <Badge variant={accountTypeColors[selectedAccount.type]}>
                  {accountTypeLabels[selectedAccount.type]}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-surface-500">Saldo</p>
                <p className="text-surface-100 font-semibold text-lg">
                  {formatCurrency(Number(selectedAccount.balance))}
                </p>
              </div>
              <div>
                <p className="text-sm text-surface-500">Status</p>
                <Badge variant={selectedAccount.status === 'ACTIVE' ? 'success' : 'warning'}>
                  {selectedAccount.status}
                </Badge>
              </div>
            </div>

            {/* Entity Info */}
            {selectedAccount.entity && (
              <div className="p-3 bg-surface-800 rounded-lg">
                <p className="text-sm text-surface-500 mb-1">Entidade</p>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-primary-400" />
                  <span className="text-surface-100 font-medium">{selectedAccount.entity.name}</span>
                  <span className="text-surface-500 text-sm">({formatDocument(selectedAccount.entity.document)})</span>
                </div>
              </div>
            )}

            {/* Bank Connection Info */}
            {selectedAccount.bankConnection && (
              <div className="p-3 bg-surface-800 rounded-lg">
                <p className="text-sm text-surface-500 mb-1">Conexão Bancária</p>
                <div className="flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-accent-400" />
                  <span className="text-surface-100 font-medium">{selectedAccount.bankConnection.name}</span>
                  <Badge variant="info">{selectedAccount.bankConnection.provider}</Badge>
                </div>
              </div>
            )}

            {/* Parent Account Info */}
            {selectedAccount.parentAccount && (
              <div className="p-3 bg-surface-800 rounded-lg">
                <p className="text-sm text-surface-500 mb-1">Conta Pai</p>
                <div className="flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-primary-400" />
                  <span className="text-surface-100 font-medium">{selectedAccount.parentAccount.name}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              {selectedAccount.pixKey && (
                <>
                  <div>
                    <p className="text-sm text-surface-500">Chave Pix</p>
                    <p className="text-surface-100 font-mono text-sm">{selectedAccount.pixKey}</p>
                  </div>
                  <div>
                    <p className="text-sm text-surface-500">Tipo da Chave</p>
                    <p className="text-surface-100">{selectedAccount.pixKeyType}</p>
                  </div>
                </>
              )}
              {selectedAccount.provider && (
                <div>
                  <p className="text-sm text-surface-500">Provider</p>
                  <p className="text-surface-100">{selectedAccount.provider}</p>
                </div>
              )}
              {selectedAccount.user && (
                <div>
                  <p className="text-sm text-surface-500">Usuário Vinculado</p>
                  <p className="text-surface-100">{selectedAccount.user.name}</p>
                  <p className="text-surface-500 text-sm">{selectedAccount.user.email}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-surface-500">Criada em</p>
                <p className="text-surface-100">{formatDate(selectedAccount.createdAt)}</p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!accountToDelete}
        onClose={() => setAccountToDelete(null)}
        title="Confirmar Exclusão"
        size="md"
      >
        {accountToDelete && (
          <div className="space-y-4">
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-400 font-medium">Atenção!</p>
                  <p className="text-surface-300 text-sm mt-1">
                    Você está prestes a excluir a conta "{accountToDelete.name}". Esta ação não pode ser desfeita.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-3 bg-surface-800 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-surface-500">Tipo:</span>
                <Badge variant={accountTypeColors[accountToDelete.type]}>
                  {accountTypeLabels[accountToDelete.type]}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-surface-500">Saldo:</span>
                <span className="text-surface-100 font-medium">
                  {formatCurrency(Number(accountToDelete.balance))}
                </span>
              </div>
              {accountToDelete.entity && (
                <div className="flex justify-between">
                  <span className="text-surface-500">Entidade:</span>
                  <span className="text-surface-100">{accountToDelete.entity.name}</span>
                </div>
              )}
            </div>

            {Number(accountToDelete.balance) > 0 && (
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-sm">
                  ⚠️ Esta conta possui saldo. Transfira ou saque os fundos antes de excluir.
                </p>
              </div>
            )}

            {deleteMutation.isError && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm">
                  {(deleteMutation.error as Error)?.message || 'Erro ao excluir conta'}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button 
                type="button" 
                variant="secondary" 
                onClick={() => setAccountToDelete(null)}
                disabled={deleteMutation.isPending}
              >
                Cancelar
              </Button>
              <Button 
                type="button" 
                variant="danger" 
                onClick={() => deleteMutation.mutate(accountToDelete.id)}
                isLoading={deleteMutation.isPending}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir Conta
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  );
}
